import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollText, History, User as UserIcon } from 'lucide-react';

type AuditRow = {
  id: string;
  admin_id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  reason: string | null;
  metadata: any;
  created_at: string;
};

const actionColor = (action: string): string => {
  if (action.includes('approve')) return 'bg-green-100 text-green-800';
  if (action.includes('reject')) return 'bg-red-100 text-red-800';
  if (action.includes('feature')) return 'bg-yellow-100 text-yellow-800';
  if (action.includes('suspend') || action.includes('delete')) return 'bg-red-100 text-red-800';
  if (action.includes('report')) return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-800';
};

export function AuditLog() {
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState<string>('all');

  const { data: rows, isLoading } = useQuery({
    queryKey: ['admin-audit-log'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_actions' as any)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data || []) as unknown as AuditRow[];
    },
  });

  // Fetch admin profile names for display
  const adminIds = Array.from(new Set((rows || []).map(r => r.admin_id)));
  const { data: profilesById } = useQuery({
    queryKey: ['audit-admin-profiles', adminIds.join(',')],
    enabled: adminIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', adminIds);
      if (error) throw error;
      return Object.fromEntries((data || []).map(p => [p.user_id, p.full_name || ''])) as Record<string, string>;
    },
  });

  const filtered = (rows || []).filter(r => {
    if (entityFilter !== 'all' && r.entity_type !== entityFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return r.action.toLowerCase().includes(q)
      || r.entity_type.toLowerCase().includes(q)
      || (r.entity_id || '').toLowerCase().includes(q)
      || (r.reason || '').toLowerCase().includes(q);
  });

  if (isLoading) {
    return <div className="space-y-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16" />)}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-primary" />
          <h2 className="font-display text-xl font-bold">Admin Audit Log</h2>
        </div>
        <span className="text-xs text-muted-foreground">{rows?.length || 0} entries (latest 500)</span>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Input
          placeholder="Search action, entity, id, or reason…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            <SelectItem value="property">Property</SelectItem>
            <SelectItem value="plot">Plot</SelectItem>
            <SelectItem value="project">Project</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="agency">Agency</SelectItem>
            <SelectItem value="developer">Developer</SelectItem>
            <SelectItem value="agent">Agent</SelectItem>
            <SelectItem value="application">Application</SelectItem>
            <SelectItem value="report">Report</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!filtered.length ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <ScrollText className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No audit entries match your filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(r => (
            <Card key={r.id} className="hover:shadow-sm transition-shadow">
              <CardContent className="p-4 flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <UserIcon className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={actionColor(r.action)}>{r.action}</Badge>
                    <span className="text-sm capitalize text-muted-foreground">{r.entity_type}</span>
                    {r.entity_id && <code className="text-xs text-muted-foreground truncate max-w-[180px]">{r.entity_id.slice(0, 8)}…</code>}
                  </div>
                  <div className="text-sm mt-1">
                    <span className="font-medium">{profilesById?.[r.admin_id] || 'Unknown admin'}</span>
                    {r.reason && <> · {r.reason}</>}
                  </div>
                  {r.metadata && Object.keys(r.metadata).length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                      {Object.entries(r.metadata).slice(0, 3).map(([k, v]) => (
                        <span key={k} className="mr-2">{k}: {String(Array.isArray(v) ? `${v.length} items` : v).slice(0, 40)}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground shrink-0">
                  {new Date(r.created_at).toLocaleString()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
